import React, { useMemo } from 'react';
import { Card, Text, Title } from '@mantine/core';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { TrendPoint } from '../../types';

interface TrendChartProps {
  trend: TrendPoint[];
}

const LINE_COLORS = [
  '#228be6',
  '#9c36b5',
  '#12b886',
  '#fd7e14',
  '#e64980',
  '#7048e8',
  '#15aabf',
  '#82c91e',
];

export const TrendChart: React.FC<TrendChartProps> = ({ trend }) => {
  const { t } = useTranslation();

  // Collect all unique section titles across all trend points
  const sectionTitles = useMemo(() => {
    const set = new Set<string>();
    for (const point of trend) {
      for (const score of point.scores) {
        set.add(score.sectionTitle);
      }
    }
    return Array.from(set);
  }, [trend]);

  // Transform data into recharts format: { cycleTitle, [sectionTitle]: overallAvg, ... }
  const data = useMemo(
    () =>
      trend.map((point) => {
        const row: Record<string, string | number | null> = {
          cycleTitle: point.cycleTitle,
        };
        for (const score of point.scores) {
          row[score.sectionTitle] = score.overallAvg;
        }
        return row;
      }),
    [trend],
  );

  if (trend.length <= 1) {
    return (
      <Card withBorder radius="md" p="lg">
        <Title order={4} mb="md">
          {t('review360.trend')}
        </Title>
        <Text c="dimmed" ta="center" py="xl">
          {t('review360.noTrendData')}
        </Text>
      </Card>
    );
  }

  return (
    <Card withBorder radius="md" p="lg">
      <Title order={4} mb="md">
        {t('review360.trend')}
      </Title>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="cycleTitle" tick={{ fontSize: 12 }} />
          <YAxis domain={[0, 5]} tickCount={6} />
          {sectionTitles.map((title, i) => (
            <Line
              key={title}
              type="monotone"
              dataKey={title}
              stroke={LINE_COLORS[i % LINE_COLORS.length]}
              strokeWidth={2}
              dot
              connectNulls
            />
          ))}
          <Tooltip />
          <Legend />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};
